import { remoteRequest } from './httpClient'
import { readSession } from './session'
import { apiItems, monitorToken, normalizeAgency, normalizeMonitor } from './normalizers'

export async function listAgencies() {
  const data = await remoteRequest('GET', '/agencies')
  return apiItems(data, ['agencies']).map(normalizeAgency).filter((agency) => agency.parent_pcc || agency.name)
}

export async function createPaymentPlan(body) {
  const data = await remoteRequest('GET', '/agencies/createPaymentPlan', {
    query: {
      price: String(Number(body.price || 0)),
      users: String(Number(body.users || 0)),
      customScripts: String(Number(body.custom_scripts || 0)),
      typeof: 'Generic',
    },
  })
  return { ...data, uuid: data?.UUID || data?.uuid }
}

export async function createAgency(body) {
  const session = readSession()
  if (!session.sabreToken) throw new Error('Token SABRE requerido')
  return remoteRequest('GET', '/agencies/createAgency', {
    sabreToken: true,
    query: {
      parentPCC: String(body.parent_pcc || '').toUpperCase(),
      name: body.name,
      description: body.description || 'Sin descripcion',
      UUID: String(body.uuid || '').toLowerCase(),
      created_user: session.email || 'Generic',
      status: '1',
    },
  })
}

export async function createAgencyPcc(body) {
  return remoteRequest('GET', '/agencies/saveUser', {
    query: {
      username: '',
      parentPcc: String(body.parent_pcc || '').toUpperCase(),
      pcc: String(body.pcc || '').toUpperCase(),
    },
  })
}

export async function createAgencyUser(body) {
  const session = readSession()
  const parentPcc = String(body.parent_pcc || '').toUpperCase()
  const agency = body.agency || {}
  const plan = agency.payment_plan || {}
  const planUuid = String(plan.uuid || agency.payment_plan_uuid || '')
  return remoteRequest('POST', '/agencies/users', {
    token: session.jwt,
    jwt: false,
    body: {
      username: String(body.username || ''),
      parentPcc,
      pcc: String(body.pcc || '').toUpperCase(),
      token: '',
      agency: {
        parentPcc,
        name: agency.name || '',
        description: agency.description || '',
        status: String(agency.status ?? '1'),
        uuidPaymentPlan: planUuid,
        paymentPlan: {
          UUID: planUuid,
          price: Number(plan.price || 0),
          users: Number(plan.users || 0),
          bostonScripts: Number(plan.boston_scripts || 0),
          customScripts: Number(plan.custom_scripts || 0),
          status: 0,
          typeof: String(plan.typeof || 'generic'),
        },
      },
    },
  })
}

export async function listParentPccs() {
  const agencies = await listAgencies()
  const byPcc = new Map()
  agencies.forEach((agency) => {
    if (!agency.parent_pcc) return
    if (!byPcc.has(agency.parent_pcc) || agency.status === '1') {
      byPcc.set(agency.parent_pcc, {
        parent_pcc: agency.parent_pcc,
        name: agency.name,
        status: agency.status,
      })
    }
  })
  return Array.from(byPcc.values()).sort((a, b) => a.parent_pcc.localeCompare(b.parent_pcc))
}

export async function listAgencyUsers(params) {
  let data
  try {
    data = await remoteRequest('GET', '/agencies/usersPCC', {
      query: { ppcc: String(params.parent_pcc || '').toUpperCase() },
    })
  } catch (_) {
    data = await remoteRequest('GET', '/getMonitor')
  }
  const pcc = String(params.pcc || '').toUpperCase()
  return apiItems(data, ['users', 'sessions'])
    .map((user) => ({
      username: String(user?.username || user?.user || user?.usuario || ''),
      parent_pcc: String(user?.parent_pcc || user?.parentPcc || user?.parentPCC || params.parent_pcc || '').toUpperCase(),
      pcc: String(user?.pcc || user?.PCC || user?.pcc_code || '').toUpperCase(),
      name: String(user?.name || user?.nombre || ''),
      token: monitorToken(user),
      created_date: user?.created_date || user?.createdDate || null,
      updated_date: user?.updated_date || user?.updatedDate || null,
    }))
    .filter((user) => user.username && (!pcc || user.pcc === pcc))
    .sort((a, b) => a.pcc.localeCompare(b.pcc) || a.username.localeCompare(b.username))
}

export async function listAgencyPccs(params) {
  const parentPcc = String(params.parent_pcc || '').toUpperCase()
  const [scriptsData, users] = await Promise.allSettled([
    remoteRequest('GET', '/agencies/pcc', { query: { ppcc: parentPcc } }),
    listAgencyUsers({ parent_pcc: parentPcc }),
  ])
  const byPcc = new Map()
  if (scriptsData.status === 'fulfilled') {
    apiItems(scriptsData.value, ['pccs', 'scripts']).forEach((row) => {
      const pcc = String(row?.pcc || '').toUpperCase()
      if (!pcc) return
      const current = byPcc.get(pcc) || { pcc, parent_pcc: parentPcc, scripts: 0, users: 0 }
      if (row?.uuid) current.scripts += 1
      byPcc.set(pcc, current)
    })
  }
  if (users.status === 'fulfilled') {
    users.value.forEach((user) => {
      const current = byPcc.get(user.pcc) || { pcc: user.pcc, parent_pcc: parentPcc, scripts: 0, users: 0 }
      current.users += 1
      byPcc.set(user.pcc, current)
    })
  }
  return Array.from(byPcc.values()).sort((a, b) => a.pcc.localeCompare(b.pcc))
}

export async function monitor() {
  const data = await remoteRequest('GET', '/getMonitor')
  return apiItems(data, ['users', 'sessions']).map(normalizeMonitor)
}

export async function getSessionToken(body) {
  const sessions = await monitor()
  const found = sessions.find((item) => (
    String(item.username).toLowerCase() === String(body.username).toLowerCase()
    && String(item.parent_pcc).toUpperCase() === String(body.parent_pcc).toUpperCase()
    && String(item.pcc).toUpperCase() === String(body.pcc).toUpperCase()
  ))
  let token = found?.token || ''
  if (!token || (token.startsWith('ATH:') && token.length < 80)) {
    const permission = await remoteRequest('GET', '/getPermission', {
      query: {
        u: body.username,
        ppcc: String(body.parent_pcc || '').toUpperCase(),
        pcc: String(body.pcc || '').toUpperCase(),
      },
    })
    token = monitorToken(permission) || String(permission || '')
  }
  if (!token) throw new Error('La sesion no tiene token')
  return { token, username: body.username }
}

export async function sabreResponse(body) {
  const tokenData = await getSessionToken(body)
  const endpoint = body.kind === 'remarks' ? '/sabre/remarksResponse' : '/sabre/reservationResponse'
  const data = await remoteRequest('GET', endpoint, {
    headers: {
      token: tokenData.token,
      Accept: 'application/json, text/plain, */*',
    },
  })
  const response = typeof data === 'object'
    ? String(data?.data || data?.raw || JSON.stringify(data, null, 2))
    : String(data || '')
  return { ok: true, kind: body.kind, response }
}
